// src/pages/Admin.js
import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../firebase";
import {
  getAllProducts, addProduct, updateProduct, deleteProduct,
  getOrders, updateOrderStatus,
  getCategories, addCategory, deleteCategory
} from "../utils/firestoreService";
import {
  getStoreSettings, saveStoreSettings,
  getSliderSettings, saveSliderSettings, DEFAULT_SLIDES
} from "../utils/settingsService";
import toast from "react-hot-toast";

// ── Constantes ────────────────────────────────────────
const EMPTY_PRODUCT = {
  name:"", shortDesc:"", description:"", price:"", originalPrice:"",
  category:"", badge:"", featured:false, active:true,
  variantStock:[], specs:""
};
const STATUS_LABELS = {
  pending:"Pendiente", confirmed:"Confirmado", preparing:"En producción",
  shipped:"Enviado", delivered:"Entregado", cancelled:"Cancelado"
};
const ACCENT_COLORS = [
  "#FF6B6B","#4DD6D0","#FFBA3D","#FF9999","#8B7CF6",
  "#F9A723","#61CACD","#FDA8AB","#FA665E","#F0E0D2"
];
const NAV_ITEMS = [
  { id:"orders",     icon:"📦", label:"Pedidos"       },
  { id:"products",   icon:"🛍️", label:"Productos"     },
  { id:"categories", icon:"🏷️", label:"Categorías"    },
  { id:"settings",   icon:"⚙️", label:"Configuración" },
  { id:"stats",      icon:"📊", label:"Estadísticas"  },
];

// ── Subir imagen a Firebase Storage ──────────────────
async function uploadImage(file, path) {
  const storageRef = ref(storage, path);
  const snap = await uploadBytes(storageRef, file);
  return await getDownloadURL(snap.ref);
}

// ── Componente gestor de imágenes del producto ────────
function ProductImageManager({ images, onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const handleAdd = async (e) => {
    const files = [...e.target.files];
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = await Promise.all(
        files.map(f => uploadImage(f, `products/${Date.now()}_${f.name}`))
      );
      onChange([...images, ...urls]);
      toast.success(`${urls.length} imagen(es) subida(s) ✓`);
    } catch { toast.error("Error al subir imágenes"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleDelete = (idx) => {
    if (!window.confirm("¿Eliminar esta imagen?")) return;
    onChange(images.filter((_, i) => i !== idx));
  };

  // Drag & drop para reordenar
  const handleDragStart = (i) => setDragIdx(i);
  const handleDragOver  = (e, i) => { e.preventDefault(); setOverIdx(i); };
  const handleDrop      = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    const reordered = [...images];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(i, 0, moved);
    onChange(reordered);
    setDragIdx(null); setOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  return (
    <div className="img-manager">
      {/* Grid de imágenes existentes */}
      {images.length > 0 && (
        <div className="img-manager-grid">
          {images.map((url, i) => (
            <div
              key={url + i}
              className={`img-manager-item ${i === 0 ? "img-manager-main" : ""} ${overIdx === i ? "img-over" : ""}`}
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragOver={e => handleDragOver(e, i)}
              onDrop={() => handleDrop(i)}
              onDragEnd={handleDragEnd}
            >
              <img src={url} alt={`Imagen ${i + 1}`}/>
              {i === 0 && <span className="img-manager-badge">Principal</span>}
              <div className="img-manager-actions">
                {i > 0 && (
                  <button
                    type="button"
                    className="img-action-btn"
                    onClick={() => {
                      const reordered = [...images];
                      [reordered[0], reordered[i]] = [reordered[i], reordered[0]];
                      onChange(reordered);
                    }}
                    title="Hacer imagen principal"
                  >⭐</button>
                )}
                <button
                  type="button"
                  className="img-action-btn img-action-delete"
                  onClick={() => handleDelete(i)}
                  title="Eliminar imagen"
                >🗑️</button>
              </div>
              <div className="img-drag-handle" title="Arrastrar para reordenar">⠿</div>
            </div>
          ))}
        </div>
      )}

      {images.length === 0 && (
        <p style={{ fontSize:13, color:"var(--text-muted)", marginBottom:12 }}>
          No hay imágenes. Sube al menos una.
        </p>
      )}

      {/* Botón subir */}
      <input ref={fileRef} type="file" multiple accept="image/*"
        onChange={handleAdd} style={{ display:"none" }}/>
      <button
        type="button"
        className="btn-upload-img"
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
      >
        {uploading ? "⏳ Subiendo..." : "📷 Agregar imágenes"}
      </button>
      {images.length > 1 && (
        <p style={{ fontSize:11, color:"var(--text-muted)", marginTop:6 }}>
          Arrastra las imágenes para cambiar el orden · La primera es la imagen principal
        </p>
      )}
    </div>
  );
}

// ── Componente uploader de imagen para slider ─────────
function SlideImageUploader({ value, slideIdx, onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [progress,  setProgress]  = useState(0);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imagenes (JPG, PNG, WebP)");
      return;
    }
    // Validar tamaño máximo 5MB
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no debe superar 5MB");
      return;
    }

    setUploading(true);
    setProgress(0);
    try {
      // Nombre limpio sin caracteres especiales
      const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `slider/slide_${slideIdx}_${Date.now()}_${cleanName}`;
      const url  = await uploadImage(file, path);
      onChange(url);
      toast.success("Imagen subida correctamente ✓");
    } catch (err) {
      // Mostrar error específico de Firebase Storage
      const code = err?.code || "";
      if (code === "storage/unauthorized") {
        toast.error("Sin permisos — verifica las reglas de Firebase Storage");
      } else if (code === "storage/canceled") {
        toast.error("Subida cancelada");
      } else if (code === "storage/unknown") {
        toast.error("Error de red — verifica tu conexión a internet");
      } else {
        toast.error(`Error al subir: ${err?.message || "desconocido"}`);
      }
      console.error("Storage upload error:", err);
    } finally {
      setUploading(false);
      setProgress(0);
      e.target.value = "";
    }
  };

  const handleDelete = () => {
    if (!window.confirm("¿Quitar esta imagen del slide?")) return;
    onChange("");
  };

  return (
    <div className="slide-img-uploader">
      {value ? (
        <div className="slide-img-preview">
          <img src={value} alt="Imagen del slide"/>
          <div className="slide-img-overlay">
            <button type="button" className="btn-change-img"
              onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? `⏳ ${progress}%` : "✏️ Cambiar"}
            </button>
            <button type="button" className="btn-delete-img"
              onClick={handleDelete} disabled={uploading}>
              🗑️ Quitar
            </button>
          </div>
        </div>
      ) : (
        <div className="slide-img-empty"
          onClick={() => !uploading && fileRef.current?.click()}
          style={{ cursor: uploading ? "not-allowed" : "pointer" }}>
          <span style={{ fontSize:32 }}>{uploading ? "⏳" : "📷"}</span>
          <p>{uploading ? `Subiendo imagen... ${progress}%` : "Clic para subir imagen"}</p>
          <span style={{ fontSize:12, color:"var(--text-muted)" }}>
            JPG, PNG, WebP · Max 5MB · Recomendado: 800x450px
          </span>
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/*"
        onChange={handleFile} style={{ display:"none" }}/>
    </div>
  );
}

// ── ADMIN PRINCIPAL ───────────────────────────────────
export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab,           setTab]           = useState("orders");
  const [sidebarOpen,   setSidebarOpen]   = useState(false);
  const [products,      setProducts]      = useState([]);
  const [orders,        setOrders]        = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [showForm,      setShowForm]      = useState(false);
  const [editing,       setEditing]       = useState(null);
  const [form,          setForm]          = useState(EMPTY_PRODUCT);
  const [productImages, setProductImages] = useState([]);
  const [simpleStock,   setSimpleStock]   = useState("");
  const [newCat,        setNewCat]        = useState({ name:"", slug:"" });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [storeSettings, setStoreSettings] = useState(null);
  const [storeLoading,  setStoreLoading]  = useState(false);
  const [slides,        setSlides]        = useState([]);
  const [slidesLoading, setSlidesLoading] = useState(false);

  useEffect(() => { if (!user) navigate("/admin/login"); }, [user, navigate]);
  useEffect(() => {
    if (tab === "orders")     loadOrders();
    if (tab === "products")   loadProducts();
    if (tab === "categories") loadCategories();
    if (tab === "settings")   loadSettings();
  }, [tab]);

  const changeTab = (t) => { setTab(t); setSidebarOpen(false); };

  const loadProducts   = async () => { setLoading(true); const p = await getAllProducts(); setProducts(p); setLoading(false); };
  const loadOrders     = async () => { setLoading(true); const o = await getOrders(); setOrders(o); setLoading(false); };
  const loadCategories = async () => { const c = await getCategories(); setCategories(c); };
  const loadSettings   = async () => {
    setStoreLoading(true); setSlidesLoading(true);
    const [s, sl] = await Promise.all([getStoreSettings(), getSliderSettings()]);
    setStoreSettings(s); setSlides(sl);
    setStoreLoading(false); setSlidesLoading(false);
  };

  const formatPrice = p => new Intl.NumberFormat("es-CO",{style:"currency",currency:"COP",minimumFractionDigits:0}).format(p);

  // Variantes
  const addVariant    = () => setForm(f => ({...f, variantStock:[...f.variantStock,{name:"",stock:0}]}));
  const removeVariant = (i) => setForm(f => ({...f, variantStock:f.variantStock.filter((_,idx)=>idx!==i)}));
  const updateVariant = (i,k,v) => setForm(f => ({
    ...f, variantStock:f.variantStock.map((vv,idx)=>idx===i?{...vv,[k]:k==="stock"?Number(v):v}:vv)
  }));
  const totalStock = form.variantStock.length>0
    ? form.variantStock.reduce((s,v)=>s+(Number(v.stock)||0),0)
    : Number(simpleStock)||0;

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_PRODUCT);
    setProductImages([]);
    setSimpleStock("");
    setShowForm(true);
  };

  const openEdit = (product) => {
    setEditing(product.id);
    setForm({...product, variantStock:product.variantStock||[], specs:JSON.stringify(product.specs||{})});
    setProductImages(product.images || []);
    setSimpleStock(product.stock || 0);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.price || !form.category) return toast.error("Nombre, precio y categoría son requeridos");
    setLoading(true);
    try {
      const hasVariants = form.variantStock.length > 0;
      const data = {
        ...form,
        price: Number(form.price),
        originalPrice: form.originalPrice ? Number(form.originalPrice) : null,
        stock: hasVariants
          ? form.variantStock.reduce((s,v)=>s+(Number(v.stock)||0),0)
          : Number(simpleStock)||0,
        variantStock: hasVariants ? form.variantStock : [],
        variants: hasVariants ? form.variantStock.map(v=>v.name) : [],
        specs: form.specs ? JSON.parse(form.specs) : {},
        images: productImages,                  // ← imágenes ya procesadas
        rating: form.rating||0,
        reviewCount: form.reviewCount||0,
        active: form.active !== false
      };
      if (editing) {
        await updateProduct(editing, data, []);  // sin files nuevos, ya están en productImages
        toast.success("Producto actualizado ✓");
      } else {
        await addProduct(data, []);
        toast.success("Producto creado ✓");
      }
      setShowForm(false);
      loadProducts();
    } catch(err) { toast.error("Error: " + err.message); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await deleteProduct(id); toast.success("Eliminado"); loadProducts();
  };

  const handleStatusChange = async (orderId, status) => {
    await updateOrderStatus(orderId, status);
    toast.success("Estado actualizado");
    setSelectedOrder(o=>({...o, status})); loadOrders();
  };

  const handleAddCategory = async () => {
    if (!newCat.name||!newCat.slug) return toast.error("Completa nombre y slug");
    await addCategory(newCat); toast.success("Categoría creada");
    setNewCat({name:"",slug:""}); loadCategories();
  };

  const handleSaveStore = async () => {
    setStoreLoading(true);
    try { await saveStoreSettings(storeSettings); toast.success("Configuración guardada ✓"); }
    catch { toast.error("Error al guardar"); }
    finally { setStoreLoading(false); }
  };

  const updateSlide = (i, key, val) =>
    setSlides(sl => sl.map((s,idx) => idx===i ? {...s,[key]:val} : s));

  const handleSaveSlides = async () => {
    setSlidesLoading(true);
    try { await saveSliderSettings(slides); toast.success("Slider actualizado ✓"); }
    catch { toast.error("Error al guardar slider"); }
    finally { setSlidesLoading(false); }
  };

  const pendingCount = orders.filter(o=>o.status==="pending").length;
  const stats = {
    totalOrders: orders.length, pending: pendingCount,
    revenue: orders.filter(o=>o.status!=="cancelled").reduce((s,o)=>s+(o.total||0),0),
    products: products.length
  };

  return (
    <div className="admin-page">
      {sidebarOpen && <div className="admin-overlay" onClick={() => setSidebarOpen(false)}/>}

      {/* SIDEBAR */}
      <aside className={`admin-sidebar ${sidebarOpen ? "admin-sidebar--open" : ""}`}>
        <div className="admin-brand">
          <span>♥</span> Admin Panel
          <button className="admin-sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
        </div>
        <nav className="admin-nav">
          {NAV_ITEMS.map(n => (
            <button key={n.id} className={tab===n.id?"active":""} onClick={() => changeTab(n.id)}>
              {n.icon} {n.label}
              {n.id==="orders" && pendingCount>0 && <span className="nav-badge">{pendingCount}</span>}
            </button>
          ))}
        </nav>
        <button className="admin-logout" onClick={() => { logout(); navigate("/"); }}>🚪 Cerrar sesión</button>
      </aside>

      <main className="admin-main">
        {/* Topbar móvil */}
        <div className="admin-topbar">
          <button className="admin-hamburger" onClick={() => setSidebarOpen(true)}>
            <span/><span/><span/>
          </button>
          <span className="admin-topbar-title">
            {NAV_ITEMS.find(n=>n.id===tab)?.icon} {NAV_ITEMS.find(n=>n.id===tab)?.label}
          </span>
          <span className="admin-topbar-brand"><span style={{color:"#FA665E"}}>♥</span> Admin</span>
        </div>

        {/* ── PEDIDOS ── */}
        {tab==="orders" && (
          <div>
            <h1>Pedidos</h1>
            {loading ? <div className="spinner"/> : (
              <div className="admin-layout-split">
                <div className="orders-list">
                  {orders.length===0 && <p className="empty-msg">No hay pedidos aún.</p>}
                  {orders.map(order => (
                    <div key={order.id}
                      className={`order-row ${selectedOrder?.id===order.id?"selected":""}`}
                      onClick={() => setSelectedOrder(order)}>
                      <div className="order-id">#{order.id.slice(0,8).toUpperCase()}</div>
                      <div className="order-customer">{order.customer?.name}</div>
                      <div className="order-total">{formatPrice(order.total)}</div>
                      <span className={`status-chip status-${order.status}`}>{STATUS_LABELS[order.status]}</span>
                    </div>
                  ))}
                </div>
                {selectedOrder && (
                  <div className="order-detail-panel">
                    <div className="panel-header">
                      <h3>#{selectedOrder.id.slice(0,8).toUpperCase()}</h3>
                      <button onClick={() => setSelectedOrder(null)}>✕</button>
                    </div>
                    <div className="panel-section">
                      <h4>Cliente</h4>
                      <p><strong>Nombre:</strong> {selectedOrder.customer?.name}</p>
                      <p><strong>Email:</strong> {selectedOrder.customer?.email}</p>
                      <p><strong>Tel:</strong> {selectedOrder.customer?.phone}</p>
                    </div>
                    <div className="panel-section">
                      <h4>Envío</h4>
                      <p>{selectedOrder.shipping?.address}</p>
                      <p>{selectedOrder.shipping?.city}, {selectedOrder.shipping?.department}</p>
                    </div>
                    <div className="panel-section">
                      <h4>Productos</h4>
                      {selectedOrder.items?.map((item,i) => (
                        <div key={i} className="panel-item">
                          <span>{item.name}{item.variant?` · ${item.variant}`:""} x{item.quantity}</span>
                          <span>{formatPrice(item.price*item.quantity)}</span>
                        </div>
                      ))}
                      <div className="panel-total">Total: {formatPrice(selectedOrder.total)}</div>
                    </div>
                    <div className="panel-section">
                      <h4>Cambiar estado</h4>
                      <select value={selectedOrder.status}
                        onChange={e => handleStatusChange(selectedOrder.id, e.target.value)}
                        className="status-select">
                        {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── PRODUCTOS ── */}
        {tab==="products" && (
          <div>
            <div className="section-header">
              <h1>Productos</h1>
              <button className="btn-primary" onClick={openNew}>+ Nuevo producto</button>
            </div>

            {showForm && (
              <div className="product-form-overlay">
                <div className="product-form">
                  <div className="form-header">
                    <h2>{editing ? "Editar producto" : "Nuevo producto"}</h2>
                    <button onClick={() => setShowForm(false)}>✕</button>
                  </div>
                  <div className="form-grid">
                    {/* Campos básicos */}
                    <div className="form-field">
                      <label>Nombre *</label>
                      <input value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))}/>
                    </div>
                    <div className="form-field">
                      <label>Categoría *</label>
                      <select value={form.category} onChange={e => setForm(f=>({...f,category:e.target.value}))}>
                        <option value="">Selecciona...</option>
                        {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Precio (COP) *</label>
                      <input type="number" value={form.price} onChange={e => setForm(f=>({...f,price:e.target.value}))}/>
                    </div>
                    <div className="form-field">
                      <label>Precio original (tachado)</label>
                      <input type="number" value={form.originalPrice||""} onChange={e => setForm(f=>({...f,originalPrice:e.target.value}))}/>
                    </div>
                    <div className="form-field">
                      <label>Badge (ej: "Nuevo")</label>
                      <input value={form.badge||""} onChange={e => setForm(f=>({...f,badge:e.target.value}))}/>
                    </div>
                    <div className="form-field">
                      <label>Descripción corta</label>
                      <input value={form.shortDesc||""} onChange={e => setForm(f=>({...f,shortDesc:e.target.value}))}/>
                    </div>
                    <div className="form-field full">
                      <label>Descripción completa</label>
                      <textarea rows={3} value={form.description||""} onChange={e => setForm(f=>({...f,description:e.target.value}))}/>
                    </div>
                    <div className="form-field full">
                      <label>Especificaciones (JSON)</label>
                      <input placeholder='{"Material":"PLA","Alto":"15cm"}' value={form.specs||""} onChange={e => setForm(f=>({...f,specs:e.target.value}))}/>
                    </div>

                    {/* Inventario / variantes */}
                    <div className="form-field full">
                      <label>Inventario</label>
                      {form.variantStock.length===0 ? (
                        <div>
                          <input type="number" min="0" placeholder="Stock (ej: 20)"
                            value={simpleStock} onChange={e => setSimpleStock(e.target.value)}
                            style={{marginTop:8}}/>
                          <button type="button" className="btn-add-variant" onClick={addVariant}>
                            + Agregar variantes (colores, tamaños…)
                          </button>
                        </div>
                      ) : (
                        <div className="variants-stock-editor">
                          <div className="variants-stock-header"><span>Variante</span><span>Stock</span><span></span></div>
                          {form.variantStock.map((v,idx) => (
                            <div key={idx} className="variant-stock-row">
                              <input placeholder="Ej: Rojo" value={v.name} onChange={e => updateVariant(idx,"name",e.target.value)}/>
                              <input type="number" min="0" value={v.stock} onChange={e => updateVariant(idx,"stock",e.target.value)}/>
                              <button type="button" className="remove-variant-btn" onClick={() => removeVariant(idx)}>✕</button>
                            </div>
                          ))}
                          <div className="variants-stock-total">Stock total: <strong>{totalStock}</strong></div>
                          <button type="button" className="btn-add-variant" onClick={addVariant}>+ Agregar variante</button>
                          <button type="button" className="btn-remove-all-variants" onClick={() => setForm(f=>({...f,variantStock:[]}))}>Quitar variantes</button>
                        </div>
                      )}
                    </div>

                    {/* ── GESTOR DE IMÁGENES ── */}
                    <div className="form-field full">
                      <label>Imágenes del producto</label>
                      <ProductImageManager
                        images={productImages}
                        onChange={setProductImages}
                      />
                    </div>

                    <div className="form-field">
                      <label>
                        <input type="checkbox" checked={form.featured||false}
                          onChange={e => setForm(f=>({...f,featured:e.target.checked}))}/>{" "}
                        Destacado en inicio
                      </label>
                    </div>
                    <div className="form-field">
                      <label>
                        <input type="checkbox" checked={form.active!==false}
                          onChange={e => setForm(f=>({...f,active:e.target.checked}))}/>{" "}
                        Activo (visible en tienda)
                      </label>
                    </div>
                  </div>
                  <div className="form-actions">
                    <button className="btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
                    <button className="btn-primary" onClick={handleSave} disabled={loading}>
                      {loading ? "Guardando..." : editing ? "Guardar cambios" : "Crear producto"}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {loading ? <div className="spinner"/> : (
              <div className="products-table">
                <div className="table-header">
                  <span>Producto</span><span>Categoría</span><span>Precio</span>
                  <span>Stock</span><span>Variantes</span><span>Estado</span><span>Acciones</span>
                </div>
                {products.map(p => (
                  <div key={p.id} className="table-row">
                    <div className="prod-cell">
                      {p.images?.[0] && <img src={p.images[0]} alt={p.name}/>}
                      <span>{p.name}</span>
                    </div>
                    <span>{p.category}</span>
                    <span>{formatPrice(p.price)}</span>
                    <span className={p.stock<=3?"low-stock":""}>{p.stock||0}</span>
                    <span>
                      {p.variantStock?.length>0
                        ? <span className="variant-count-badge">{p.variantStock.length} var.</span>
                        : <span style={{color:"var(--text-muted)",fontSize:12}}>Sin var.</span>}
                    </span>
                    <span className={`status-chip ${p.active?"status-confirmed":"status-cancelled"}`}>
                      {p.active ? "Activo" : "Inactivo"}
                    </span>
                    <div className="row-actions">
                      <button onClick={() => openEdit(p)}>✏️</button>
                      <button onClick={() => handleDelete(p.id)}>🗑️</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── CATEGORÍAS ── */}
        {tab==="categories" && (
          <div>
            <h1>Categorías</h1>
            <div className="cat-form">
              <input placeholder="Nombre (ej: Decoración)" value={newCat.name} onChange={e => setNewCat(c=>({...c,name:e.target.value}))}/>
              <input placeholder="Slug (ej: decoracion)" value={newCat.slug} onChange={e => setNewCat(c=>({...c,slug:e.target.value}))}/>
              <button className="btn-primary" onClick={handleAddCategory}>+ Agregar</button>
            </div>
            <div className="cats-list">
              {categories.map(c => (
                <div key={c.id} className="cat-item">
                  <span>{c.name}</span><span className="cat-slug">/{c.slug}</span>
                  <button onClick={() => { deleteCategory(c.id); loadCategories(); toast.success("Eliminada"); }}>🗑️</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CONFIGURACIÓN ── */}
        {tab==="settings" && (
          <div>
            <h1>Configuración</h1>

            {/* Datos de la tienda */}
            <div className="settings-card">
              <h2 className="settings-section-title">📱 Contacto y redes sociales</h2>
              {storeLoading ? <div className="spinner"/> : storeSettings && (
                <>
                  <div className="settings-grid">
                    <div className="form-field">
                      <label>📱 WhatsApp (con código país, sin +)</label>
                      <input placeholder="573102631621" value={storeSettings.whatsapp||""} onChange={e => setStoreSettings(s=>({...s,whatsapp:e.target.value}))}/>
                      <span className="field-hint">Ej: 573102631621</span>
                    </div>
                    <div className="form-field">
                      <label>📸 Instagram (solo el usuario)</label>
                      <input placeholder="conelcorazonco" value={storeSettings.instagram||""} onChange={e => setStoreSettings(s=>({...s,instagram:e.target.value}))}/>
                    </div>
                    <div className="form-field">
                      <label>📧 Email general</label>
                      <input type="email" value={storeSettings.email||""} onChange={e => setStoreSettings(s=>({...s,email:e.target.value}))}/>
                    </div>
                    <div className="form-field">
                      <label>📧 Email de pagos</label>
                      <input type="email" value={storeSettings.emailPagos||""} onChange={e => setStoreSettings(s=>({...s,emailPagos:e.target.value}))}/>
                    </div>
                    <div className="form-field">
                      <label>📞 Teléfono</label>
                      <input value={storeSettings.phone||""} onChange={e => setStoreSettings(s=>({...s,phone:e.target.value}))}/>
                    </div>
                    <div className="form-field">
                      <label>📍 Dirección / Ciudad</label>
                      <input value={storeSettings.address||""} onChange={e => setStoreSettings(s=>({...s,address:e.target.value}))}/>
                    </div>
                  </div>
                  <h2 className="settings-section-title" style={{marginTop:32}}>💳 Datos de pago</h2>
                  <div className="settings-grid">
                    <div className="form-field"><label>Nequi / Daviplata</label><input value={storeSettings.nequi||""} onChange={e => setStoreSettings(s=>({...s,nequi:e.target.value}))}/></div>
                    <div className="form-field"><label>Bancolombia</label><input value={storeSettings.bancolombia||""} onChange={e => setStoreSettings(s=>({...s,bancolombia:e.target.value}))}/></div>
                    <div className="form-field"><label>Titular</label><input value={storeSettings.titular||""} onChange={e => setStoreSettings(s=>({...s,titular:e.target.value}))}/></div>
                  </div>
                  <h2 className="settings-section-title" style={{marginTop:32}}>🚚 Envíos</h2>
                  <div className="settings-grid">
                    <div className="form-field">
                      <label>Monto mínimo envío gratis (COP)</label>
                      <input type="number" value={storeSettings.freeShippingMin||""} onChange={e => setStoreSettings(s=>({...s,freeShippingMin:Number(e.target.value)}))}/>
                    </div>
                    <div className="form-field">
                      <label>Mensaje envío gratis</label>
                      <input value={storeSettings.shippingMsg||""} onChange={e => setStoreSettings(s=>({...s,shippingMsg:e.target.value}))}/>
                    </div>
                  </div>
                  <div style={{marginTop:24}}>
                    <button className="btn-primary" onClick={handleSaveStore} disabled={storeLoading}>
                      {storeLoading ? "Guardando..." : "💾 Guardar configuración"}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Editor slider */}
            <div className="settings-card" style={{marginTop:24}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20,flexWrap:"wrap",gap:12}}>
                <h2 className="settings-section-title" style={{marginBottom:0}}>🎠 Slider página principal</h2>
                <div style={{display:"flex",gap:10}}>
                  <button className="btn-ghost" onClick={() => { setSlides([...DEFAULT_SLIDES]); toast("Slides restaurados"); }} style={{fontSize:13,padding:"8px 16px"}}>↺ Restaurar</button>
                  <button className="btn-primary" onClick={handleSaveSlides} disabled={slidesLoading} style={{padding:"8px 20px"}}>
                    {slidesLoading ? "Guardando..." : "💾 Guardar slider"}
                  </button>
                </div>
              </div>
              <p style={{fontSize:13,color:"var(--text-muted)",marginBottom:24}}>
                Sube una imagen desde tu dispositivo o deja el campo vacío para usar el emoji.
              </p>

              {slidesLoading ? <div className="spinner"/> : (
                <div className="slides-editor">
                  {slides.map((slide, i) => (
                    <div key={slide.id||i} className="slide-editor-card">
                      <div className="slide-editor-header">
                        <span className="slide-emoji-preview">{slide.emoji}</span>
                        <span className="slide-num">Slide {i+1} — {slide.title}</span>
                        <div className="slide-accent-preview" style={{background:slide.accent}}/>
                      </div>

                      <div className="settings-grid">
                        <div className="form-field"><label>Etiqueta</label><input value={slide.label||""} onChange={e => updateSlide(i,"label",e.target.value)}/></div>
                        <div className="form-field"><label>Tag de tendencia</label><input value={slide.tag||""} onChange={e => updateSlide(i,"tag",e.target.value)}/></div>
                        <div className="form-field full"><label>Título principal</label><input value={slide.title||""} onChange={e => updateSlide(i,"title",e.target.value)}/></div>
                        <div className="form-field full"><label>Descripción</label><textarea rows={2} value={slide.desc||""} onChange={e => updateSlide(i,"desc",e.target.value)}/></div>
                        <div className="form-field"><label>Emoji (si no hay imagen)</label><input value={slide.emoji||""} onChange={e => updateSlide(i,"emoji",e.target.value)}/></div>
                        <div className="form-field">
                          <label>Color de acento</label>
                          <div className="accent-picker">
                            {ACCENT_COLORS.map(c => (
                              <button key={c} type="button"
                                className={`accent-swatch ${slide.accent===c?"selected":""}`}
                                style={{background:c}} onClick={() => updateSlide(i,"accent",c)} title={c}/>
                            ))}
                            <input type="color" value={slide.accent||"#FF6B6B"}
                              onChange={e => updateSlide(i,"accent",e.target.value)}
                              style={{width:36,height:36,padding:2,border:"1.5px solid var(--border)",borderRadius:8,cursor:"pointer"}}/>
                          </div>
                        </div>

                        {/* ── UPLOADER DE IMAGEN DEL SLIDE ── */}
                        <div className="form-field full">
                          <label>Imagen del slide</label>
                          <SlideImageUploader
                            value={slide.img||""}
                            slideIdx={i}
                            onChange={url => updateSlide(i,"img",url)}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── ESTADÍSTICAS ── */}
        {tab==="stats" && (
          <div>
            <h1>Estadísticas</h1>
            <div className="stats-grid">
              <div className="stat-card"><div className="stat-num">{stats.totalOrders}</div><div className="stat-label">Pedidos totales</div></div>
              <div className="stat-card"><div className="stat-num">{stats.pending}</div><div className="stat-label">Pendientes</div></div>
              <div className="stat-card"><div className="stat-num">{formatPrice(stats.revenue)}</div><div className="stat-label">Ingresos</div></div>
              <div className="stat-card"><div className="stat-num">{stats.products}</div><div className="stat-label">Productos</div></div>
            </div>
            <div>
              <h3 style={{fontFamily:"var(--font-display)",marginBottom:20,color:"var(--ink)"}}>Pedidos por estado</h3>
              {Object.entries(STATUS_LABELS).map(([k,v]) => {
                const count = orders.filter(o=>o.status===k).length;
                return count>0 ? (
                  <div key={k} className="status-bar">
                    <span>{v}</span>
                    <div className="bar-wrap"><div className="bar-fill" style={{width:`${(count/stats.totalOrders)*100}%`}}/></div>
                    <span>{count}</span>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
